import {
  BuildingOffice2Icon,
  ClockIcon,
  TruckIcon,
} from "@heroicons/react/24/outline";
import { Link } from "@tanstack/react-router";
import { DotMenu } from "components/DotMenu";
import { formatDate } from "helpers/date";
import {
  DetailTile,
  FacilityTile,
} from "pages/app/uppdrag/operator/DetailTiles";
import { useEffect, useRef, useState } from "react";
import {
  TransportSedel,
  TransportSedelStatus,
} from "types/transportSedel.type";
import { Uppdrag } from "types/uppdrag.type";
import { OperatorDangerSheet } from "./OperatorDangerSheet";
import { OperatorTransportSedelSheet } from "./OperatorTransportSedelSheet";
import { OperatorDepartureSheet } from "./OperatorDepartureSheet";
import { OperatorInlineTips } from "./OperatorInlineTips";
import { OperatorVehicleSheet } from "./OperatorVehicleSheet";
import { OperatorWasteSheet } from "./OperatorWasteSheet";
import { OperatorWasteStationSheet } from "./OperatorWasteStationSheet";
import { useUpdateDepartureLogic } from "./useUpdateDepartureLogic";
import { useUpdateVehicleLogic } from "./useUpdateVehicleLogic";
import { useUpdateWasteStationLogic } from "./useUpdateWasteStationLogic";
import { useCreateWasteLogic } from "../useCreateWasteLogic";
import { useDeleteTransportSedelLogic } from "../useDeleteTransportSedelLogic";
import { useUpdateTransportSedelLogic } from "../useUpdateTransportSedelLogic";
import { useLoadedCelebration } from "./useLoadedCelebration";
import "./loadedCelebration.css";
import { OperatorChecklist } from "./OperatorChecklist";
import { OperatorSignatureSection } from "./OperatorSignatureSection";
import { OperatorStatusStepper } from "./OperatorStatusStepper";
import { OperatorWasteSection } from "./OperatorWasteSection";
import {
  getCompletionRequirements,
  getLoadingRequirements,
  RequirementKey,
  SignatureRequirementKey,
  wasteHasFinalWeight,
  wasteHasWeight,
} from "./loadingRequirements";
import { WasteEditRequest } from "./OperatorWasteSection";

type Props = {
  transportSedel: TransportSedel;
  uppdrag: Uppdrag;
};

export const TransportSedelItemOperator = ({
  transportSedel,
  uppdrag,
}: Props) => {
  const updateTransportSedelLogic = useUpdateTransportSedelLogic(
    uppdrag,
    transportSedel,
  );
  const deleteTransportSedelLogic =
    useDeleteTransportSedelLogic(transportSedel);
  const updateWasteStationLogic = useUpdateWasteStationLogic(transportSedel);
  const updateVehicleLogic = useUpdateVehicleLogic(transportSedel);
  const updateDepartureLogic = useUpdateDepartureLogic(transportSedel);
  const createWasteLogic = useCreateWasteLogic({ transportSedel, uppdrag });
  const { isCelebrating } = useLoadedCelebration(transportSedel);

  const loadingRequirements = getLoadingRequirements(transportSedel);
  const completionRequirements = getCompletionRequirements(transportSedel);

  const signatureSectionRef = useRef<HTMLElement>(null);
  const wasteSectionRef = useRef<HTMLElement>(null);
  const [highlightedSection, setHighlightedSection] = useState<
    "signatures" | "waste" | null
  >(null);

  useEffect(() => {
    if (!highlightedSection) return;
    const timeout = setTimeout(() => setHighlightedSection(null), 2000);
    return () => clearTimeout(timeout);
  }, [highlightedSection]);

  const scrollToSection = (section: "signatures" | "waste") => {
    const ref =
      section === "signatures" ? signatureSectionRef : wasteSectionRef;
    ref.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    setHighlightedSection(section);
  };

  // Modaler som ägs av under-sektionerna öppnas via request-props
  const [signatureRequest, setSignatureRequest] =
    useState<SignatureRequirementKey | null>(null);
  const [wasteEditRequest, setWasteEditRequest] =
    useState<WasteEditRequest | null>(null);

  // Samma ordning som avfallslistan visas i
  const wasteListSortedById = transportSedel.wasteList
    .slice()
    .sort((a, b) => (a.id < b.id ? -1 : 1));

  const onChecklistAction = (key: RequirementKey) => {
    switch (key) {
      case "wasteStation":
        updateWasteStationLogic.openModal();
        break;
      case "vehicle":
        updateVehicleLogic.openModal();
        break;
      case "timeOfDeparture":
        updateDepartureLogic.openModal();
        break;
      case "wasteWeight": {
        const wasteMissingWeight = wasteListSortedById.find(
          (w) => !wasteHasWeight(w),
        );
        if (transportSedel.wasteList.length === 0) {
          createWasteLogic.openModal();
        } else if (wasteMissingWeight) {
          setWasteEditRequest({
            wasteId: wasteMissingWeight.id,
            highlightField: "weightPreliminary",
          });
        } else {
          scrollToSection("waste");
        }
        break;
      }
      case "finalWeight": {
        const wasteMissingFinalWeight = wasteListSortedById.find(
          (w) => !wasteHasFinalWeight(w),
        );
        if (transportSedel.wasteList.length === 0) {
          createWasteLogic.openModal();
        } else if (wasteMissingFinalWeight) {
          setWasteEditRequest({
            wasteId: wasteMissingFinalWeight.id,
            highlightField: "weightFinal",
          });
        } else {
          scrollToSection("waste");
        }
        break;
      }
      case "driverSignature":
      case "customerSignature":
      case "wasteStationSignature":
        setSignatureRequest(key);
        break;
    }
  };

  return (
    <div className="max-w-3xl overflow-hidden rounded-2xl border border-gray-100 bg-white shadow">
      <div className="flex items-start justify-between gap-x-4 border-b border-gray-75 bg-gray-12 px-4 pb-3 pt-4 sm:px-6">
        <div className="flex-1">
          <OperatorStatusStepper
            status={transportSedel.status}
            celebrateStatus={
              isCelebrating ? TransportSedelStatus.LoadedOnTruck : null
            }
          />
        </div>
        <DotMenu
          items={[
            {
              label: "Redigera",
              onClick: updateTransportSedelLogic.openModal,
            },
            {
              label: "Ta bort",
              onClick: deleteTransportSedelLogic.openModal,
              isDelete: true,
            },
          ]}
        />
      </div>

      <div className="flex flex-col gap-y-6 px-4 py-5 sm:px-6">
        {transportSedel.status === TransportSedelStatus.LoadedOnTruck && (
          <OperatorInlineTips id="operator-lastad-last-page-tips">
            Nu är allt inmatat för en giltig transportsedel. Gå till Last-sidan
            för att signera och väga allt avfall i fordonet på en gång.
          </OperatorInlineTips>
        )}
        {transportSedel.status === TransportSedelStatus.Created && (
          <OperatorChecklist
            title="Kvar innan lastning"
            doneText="Transportsedeln är redo att lastas."
            requirements={loadingRequirements}
            onAction={onChecklistAction}
          />
        )}
        {transportSedel.status === TransportSedelStatus.LoadedOnTruck && (
          <OperatorChecklist
            title="Kvar innan slutförande"
            doneText="Transportsedeln är redo att slutföras."
            requirements={completionRequirements}
            onAction={onChecklistAction}
          />
        )}

        <section>
          <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
            Detaljer
          </h4>
          <div className="grid grid-cols-1 gap-2 xs:grid-cols-2">
            <FacilityTile facility={uppdrag.facility} />
            <DetailTile
              icon={BuildingOffice2Icon}
              label="Mottagarstation"
              value={transportSedel.wasteStation?.name}
              onClick={updateWasteStationLogic.openModal}
            />
            <DetailTile
              icon={TruckIcon}
              label="Fordon"
              value={transportSedel.vehicle?.registrationNumber}
              onClick={updateVehicleLogic.openModal}
            />
            <DetailTile
              icon={ClockIcon}
              label="Avfärd"
              value={
                transportSedel.timeOfDepartureUtc
                  ? formatDate(transportSedel.timeOfDepartureUtc)
                  : undefined
              }
              onClick={updateDepartureLogic.openModal}
            />
          </div>
        </section>

        <OperatorWasteSection
          ref={wasteSectionRef}
          transportSedel={transportSedel}
          uppdrag={uppdrag}
          createWasteLogic={createWasteLogic}
          isHighlighted={highlightedSection === "waste"}
          wasteEditRequest={wasteEditRequest}
          onWasteEditRequestHandled={() => setWasteEditRequest(null)}
        />

        <OperatorSignatureSection
          ref={signatureSectionRef}
          transportSedel={transportSedel}
          uppdrag={uppdrag}
          isHighlighted={highlightedSection === "signatures"}
          signatureRequest={signatureRequest}
          onSignatureRequestHandled={() => setSignatureRequest(null)}
        />
      </div>

      <OperatorTransportSedelSheet
        title={"Redigera transportsedel"}
        logic={updateTransportSedelLogic}
      />
      <OperatorWasteStationSheet logic={updateWasteStationLogic} />
      <OperatorVehicleSheet logic={updateVehicleLogic} />
      <OperatorDepartureSheet logic={updateDepartureLogic} />
      <OperatorDangerSheet
        isOpen={deleteTransportSedelLogic.isModalOpen}
        onClose={deleteTransportSedelLogic.onModalClose}
        actionHandler={deleteTransportSedelLogic.onConfirm}
        isLoading={deleteTransportSedelLogic.mutation.isPending}
        title="Ta bort transportsedeln?"
        paragraphs={["Detta går inte att ångra."]}
        actionText="Ta bort"
      />
      <OperatorWasteSheet logic={createWasteLogic} title={"Nytt avfall"} />
    </div>
  );
};
